import { ref, watch } from 'vue';
import io from 'socket.io-client';
import JitsiService from '@/Services/JitsiService';
import useSessionStore from '@/Stores/useSessionStore';
import {
  DEVICE_SCREEN_SHARE_APP_DOMAIN,
  DEVICE_WEBSOCKET_ADDRESS,
  DEVICE_WEBSOCKET_PORT,
  VICE_SESSION_PHASES,
  TURN_CREDENTIALS,
  DISPLAY_NAME,
} from '@/Utils/vice-constants';

function useSmartphoneScreenshare() {
  const sessionStore = ref();
  const currentUser = ref(null);
  const otherUser = ref(null);
  const socketRef = ref();
  const heightRef = ref(640);
  const widthRef = ref(360);
  const peerRef = ref();

  const mediaStream = ref(null);
  const remoteTracks = ref([]);
  const jitsiVideoTrack = ref(null);
  const jitsiAudioTrack = ref(null);
  const jitsiRoom = ref(null);

  const Types = {
    Offer: 'Offer',
    Joined: 'Joined',
    SignIn: 'SignIn',
    Answer: 'Answer',
    EndCall: 'EndCall',
    IceCandidates: 'IceCandidates',
    StartStreaming: 'StartStreaming',
  };

  async function connect() {
    sessionStore.value = useSessionStore();
    const jitsi = JitsiService();
    const { room } = await jitsi.makeConnection(sessionStore.value.sessionId);
    jitsiRoom.value = room;
    room.setDisplayName(`${DISPLAY_NAME.SMARTPHONE}`);

    initializeSocket();
    initializePeer();

    socketRef.value.on(Types.Answer, handleAnswer);
    socketRef.value.on(Types.Offer, handleReceiveCall);
    socketRef.value.on(Types.EndCall, stopSmartphoneStream);
    socketRef.value.on(Types.StartStreaming, handleStartStreaming);
    socketRef.value.on(Types.IceCandidates, handleNewICECandidateMsg);
  }

  function initializeSocket() {
    const socket = io(`${DEVICE_WEBSOCKET_ADDRESS}:${DEVICE_WEBSOCKET_PORT}`, {
      withCredentials: false,
      extraHeaders: {
        'Access-Control-Allow-Origin': '*',
      },
    });

    socketRef.value = socket;
    currentUser.value = socketRef.value.id;

    const code = sessionStore.value.sessionId;
    const streamer = true;
    socketRef.value = socket;

    currentUser.value = socketRef.value.id;

    if (currentUser.value) {
      signInUser(code, streamer);
    } else {
      socket.on('connect', () => {
        currentUser.value = socketRef.value.id;
        signInUser(code, streamer);
      });
    }
  }

  function signInUser(code, streamer) {
    socketRef.value.emit(
      'message',
      JSON.stringify({
        type: Types.SignIn,
        create: streamer,
        sessionId: code,
        username: currentUser.value,
      }),
    );
  }

  function initializePeer() {
    const peerConnectionConfig = {
      iceServers: [
        {
          urls: `turns:${DEVICE_SCREEN_SHARE_APP_DOMAIN}:5349?transport=tcp`,
          username: TURN_CREDENTIALS,
          credential: TURN_CREDENTIALS,
        },
        {
          urls: `stun:stun.l.google.com:19302`,
        },
      ],
      iceTransportPolicy: 'all',
    };

    peerRef.value = createPeer(peerConnectionConfig);
  }

  function createPeer(configRTC) {
    const peer = new RTCPeerConnection(configRTC);

    peer.ontrack = handleTrackEvent;
    peer.onicecandidate = handleICECandidateEvent;
    peer.onnegotiationneeded = handleNegotiationNeededEvent;

    peer.onsignalingstatechange = (e) =>
      console.log(`SIGNALING: ${e.currentTarget.signalingState}`);
    peer.onconnectionstatechange = async (e) => {
      console.log(`CONNECTION: ${e.currentTarget.connectionState}`, e);
      if (e.currentTarget.connectionState === 'connected') {
        jitsiRoom.value.join();
      }

      if (e.currentTarget.connectionState === 'disconnected') {
        await stopSmartphoneStream();
        peer.close();
      }
    };

    return peer;
  }

  async function closePeerConnection() {
    if (!peerRef.value) return;

    try {
      const transceivers = peerRef.value.getTransceivers();
      transceivers.forEach((transceiver) => {
        if (transceiver.sender) {
          transceiver.sender.track?.stop();
        }
        if (transceiver.stop) {
          transceiver.stop();
        }
      });

      peerRef.value.ontrack = null;
      peerRef.value.onicecandidate = null;
      peerRef.value.onnegotiationneeded = null;
      peerRef.value.onsignalingstatechange = null;
      peerRef.value.onconnectionstatechange = null;

      peerRef.value.close();

      console.log('Peer connection closed successfully');
    } catch (error) {
      console.error('Error closing peer connection:', error);
    }
  }

  function handleStartStreaming(data) {
    data = JSON.parse(data);
    otherUser.value = data.username;
    handleNegotiationNeededEvent();
  }

  function handleNegotiationNeededEvent() {
    if (peerRef.value.connectionState !== 'closed') {
      peerRef.value
        .createOffer({ offerToReceiveVideo: true, offerToReceiveAudio: true })
        .then((offer) => peerRef.value.setLocalDescription(offer))
        .then(() => {
          const payload = {
            type: Types.Offer,
            target: otherUser.value,
            username: currentUser.value,
            data: peerRef.value.localDescription.sdp,
          };
          socketRef.value.emit('message', JSON.stringify(payload));
        })
        .catch((e) => console.error(e.message));
    }
  }

  function handleReceiveCall(incoming) {
    incoming = JSON.parse(incoming);

    if (peerRef.value.connectionState !== 'closed') {
      const desc = new RTCSessionDescription({ type: 'offer', sdp: incoming.data });

      peerRef.value
        .setRemoteDescription(desc)
        .then(() =>
          peerRef.value.createAnswer({ offerToReceiveVideo: true, offerToReceiveAudio: true }),
        )
        .then((answer) => peerRef.value.setLocalDescription(answer))
        .then(() => {
          const payload = {
            type: Types.Answer,
            target: incoming.username,
            username: currentUser.value,
            data: peerRef.value.localDescription.sdp,
          };
          socketRef.value.emit('message', JSON.stringify(payload));
        })
        .catch((e) => console.error(e.message));
    }
  }

  function handleAnswer(message) {
    message = JSON.parse(message);

    if (peerRef.value.connectionState !== 'closed') {
      const desc = new RTCSessionDescription({ type: 'answer', sdp: message.data });
      peerRef.value.setRemoteDescription(desc);
    }
  }

  function handleICECandidateEvent(event) {
    if (event.candidate) {
      const payload = {
        type: Types.IceCandidates,
        username: currentUser.value,
        target: otherUser.value,
        data: JSON.stringify(event.candidate),
      };
      socketRef.value.emit('message', JSON.stringify(payload));
    }
  }

  function handleNewICECandidateMsg(incoming) {
    incoming = JSON.parse(incoming);
    incoming.data = JSON.parse(incoming.data);

    if (peerRef.value.connectionState !== 'closed') {
      const candidate = new RTCIceCandidate(incoming.data);
      peerRef.value.addIceCandidate(candidate);
    }
  }

  async function publishMediaStreams(conference, stream, mediaType) {
    try {
      const trackInfo = {
        stream,
        sourceType: 'stream',
        mediaType,
        videoType: mediaType === 'video' ? 'camera' : undefined,
        constraints: {
          height: {
            ideal: 4096,
          },
          width: {
            ideal: 4096,
          },
        },
      };

      const jitsiTracks = await JitsiMeetJS.createLocalTracksFromMediaStreams([trackInfo]);
      jitsiTracks.forEach((jitsiTrack) => {
        if (jitsiTrack.type === 'video') {
          jitsiVideoTrack.value = jitsiTrack;
        } else if (jitsiTrack.type === 'audio') {
          jitsiAudioTrack.value = jitsiTrack;
        }

        conference.addTrack(jitsiTrack);
      });
    } catch (error) {
      console.error('Error publishing streams:', error);
    }
  }

  async function handleTrackEvent(remoteTrack) {
    let isLandscape = false;
    const video = document.getElementById('screencast-video');
    video.srcObject = mediaStream.value;
    video.autoplay = true;

    if (!mediaStream.value) {
      mediaStream.value = new MediaStream();
    }

    remoteTracks.value.push(remoteTrack);
    mediaStream.value.addTrack(remoteTrack.track.clone());

    if (remoteTrack.track.kind === 'video') {
      video.onloadedmetadata = () => {
        if (updateCanvasDimensions()) {
          isLandscape = video.videoWidth > video.videoHeight;
        }
      };

      const checkOrientation = setInterval(() => {
        if (video.videoWidth > 0 && video.videoHeight > 0) {
          widthRef.value = video.videoWidth;
          heightRef.value = video.videoHeight;

          const newIsLandscape = video.videoWidth > video.videoHeight;
          if (newIsLandscape !== isLandscape) {
            isLandscape = newIsLandscape;
            // Orientation has been changed to landscape||portrait
            updateCanvasDimensions();
          }
        }
      }, 500);

      remoteTrack.onended = () => {
        clearInterval(checkOrientation);
        remoteTrack.remove();
      };
    }

    if (remoteTracks.value.length === 2) {
      await publishMediaStreams(jitsiRoom.value, mediaStream.value, 'audio');

      const canvas = document.getElementById('screencast-canvas');
      const ctx = canvas.getContext('2d');

      if (!updateCanvasDimensions() && canvas.width === 0 && canvas.height === 0) {
        canvas.width = 640;
        canvas.height = 480;
      }

      renderCanvasFrame(canvas, ctx, video);
      const canvasStream = canvas.captureStream(18);

      await publishMediaStreams(jitsiRoom.value, canvasStream, 'video');
    }
  }

  function renderCanvasFrame(canvas, ctx, video) {
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    requestAnimationFrame(() => renderCanvasFrame(canvas, ctx, video));
  }

  function updateCanvasDimensions() {
    const canvas = document.getElementById('screencast-canvas');
    const video = document.getElementById('screencast-video');

    if (video.videoWidth > 0 && video.videoHeight > 0) {
      canvas.height = video.videoHeight;
      canvas.width = video.videoWidth;

      heightRef.value = video.videoHeight;
      widthRef.value = video.videoWidth;
      return true;
    }
    return false;
  }

  async function stopSmartphoneStream(withSignal = false) {
    try {
      await closeJitsiConnection();
      if (withSignal) endCall();
      await closePeerConnection();

      sessionStore.value.smartphoneStreamId = null;
      mediaStream.value = null;
      remoteTracks.value = [];
    } catch (error) {
      console.error('Error stopping Smartphone Stream:', error);
    }
  }

  async function closeJitsiConnection() {
    if (jitsiVideoTrack.value === undefined || jitsiVideoTrack.value === null) {
      console.log('No smartphone stream to stop');
      jitsiRoom.value.leave();
      return;
    }

    await jitsiVideoTrack.value?.setEffect(undefined);
    await jitsiVideoTrack.value?.dispose();
    await jitsiRoom.value?.removeTrack(jitsiVideoTrack.value);

    jitsiVideoTrack.value.stream?.getTracks().forEach((track) => track.stop());
    jitsiVideoTrack.value.track.stop();

    mediaStream.value?.getTracks().forEach((track) => track.stop());

    if (jitsiAudioTrack.value !== null) {
      await jitsiRoom.value.removeTrack(jitsiAudioTrack.value);
    }

    jitsiVideoTrack.value = null;
    jitsiAudioTrack.value = null;

    if (jitsiRoom.value.room !== null) {
      jitsiRoom.value.leave();
    }
  }

  function endCall() {
    const payload = {
      type: Types.EndCall,
      username: currentUser.value,
      target: otherUser.value,
    };
    socketRef.value.emit('message', JSON.stringify(payload));
  }

  watch(
    () => sessionStore.value?.currentPhase,
    (phase) => {
      if (phase !== VICE_SESSION_PHASES.CONSULTATION) {
        stopSmartphoneStream(true).then(() => console.log('Smartphone stream stopped'));
      }
    },
  );

  return {
    connect,
    stopSmartphoneStream,
  };
}

export default useSmartphoneScreenshare;
